import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import AdminLayout from './AdminLayout';
import {
  TrendingUp, Users, Briefcase, Building2,
  Globe, Award, CheckCircle, XCircle,
  Clock, Star, Zap, Crown, ArrowUp,
  BarChart2, PieChart, Activity, Download
} from 'lucide-react';

const STATUS_COLORS = {
  pending: '#F59E0B',
  reviewed: '#4F8EF7',
  shortlisted: '#10B981',
  interview_scheduled: '#7C3AED',
  hired: '#22D3EE',
  rejected: '#EF4444',
  withdrawn: '#94A3B8',
};

const PLAN_CONFIG = {
  trial: { color: '#94A3B8', bg: 'rgba(148,163,184,0.1)', label: 'Trial', icon: '⏰' },
  basic: { color: '#4F8EF7', bg: 'rgba(79,142,247,0.1)', label: 'Basic', icon: '⭐' },
  pro: { color: '#7C3AED', bg: 'rgba(124,58,237,0.1)', label: 'Pro', icon: '🚀' },
  enterprise: { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', label: 'Enterprise', icon: '👑' },
};

export default function AdminStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);

  useEffect(() => { load(); }, [range]);

  const load = async () => {
    setLoading(true);
    try {
      const since = new Date(
        Date.now() - range * 24 * 60 * 60 * 1000
      ).toISOString();

      const [companiesRes, jobsRes, appsRes, studentsRes] =
        await Promise.all([
          supabase.from('companies').select(
            'id, name, country, verification_status, plan, created_at, slots_used, contact_email, reg_certificate_url, is_verified'
          ).eq('account_deleted', false),
          supabase.from('job_listings').select(
            'id, title, category, job_type, is_active, company_id, applications_count, views_count, created_at'
          ),
          supabase.from('job_applications').select(
            'id, status, applied_at, company_id, job_listing_id'
          ),
          supabase.from('student_career_profiles').select('id').limit(1),
        ]);

      const companies = companiesRes.data || [];
      const jobs = jobsRes.data || [];
      const apps = appsRes.data || [];

      // ── Platform totals ──────────────────────────
      const totalCompanies = companies.length;
      const verifiedCompanies = companies.filter(c => c.verification_status === 'verified').length;
      const pendingCompanies = companies.filter(c => c.verification_status === 'pending').length;
      const activeJobs = jobs.filter(j => j.is_active).length;
      const totalApps = apps.length;
      const hiredCount = apps.filter(a => a.status === 'hired').length;
      const rejectedCount = apps.filter(a => a.status === 'rejected').length;
      const shortlistedCount = apps.filter(a =>
        ['shortlisted', 'interview_scheduled'].includes(a.status)
      ).length;

      // ── Plan breakdown ───────────────────────────
      const planBreakdown = {};
      companies.forEach(c => {
        const p = c.plan || 'trial';
        planBreakdown[p] = (planBreakdown[p] || 0) + 1;
      });

      // ── Companies by country ─────────────────────
      const byCountry = {};
      companies.forEach(c => {
        if (c.country) byCountry[c.country] = (byCountry[c.country] || 0) + 1;
      });
      const companiesByCountry = Object.entries(byCountry)
        .sort((a, b) => b[1] - a[1]).slice(0, 8)
        .map(([country, count]) => ({ country, count }));

      // ── Jobs by category ─────────────────────────
      const byCategory = {};
      jobs.forEach(j => {
        if (j.category) byCategory[j.category] = (byCategory[j.category] || 0) + 1;
      });
      const jobsByCategory = Object.entries(byCategory)
        .sort((a, b) => b[1] - a[1]).slice(0, 8)
        .map(([category, count]) => ({ category, count }));

      // ── Applications by status ───────────────────
      const byStatus = {};
      apps.forEach(a => {
        byStatus[a.status] = (byStatus[a.status] || 0) + 1;
      });
      const applicationsByStatus = Object.entries(byStatus)
        .sort((a, b) => b[1] - a[1])
        .map(([status, count]) => ({ status, count }));

      // ── Top companies by applications received ───
      const appsByCompany = {};
      apps.forEach(a => {
        if (a.company_id) {
          appsByCompany[a.company_id] =
            (appsByCompany[a.company_id] || 0) + 1;
        }
      });

      const topByApps = companies
        .filter(c => appsByCompany[c.id])
        .map(c => ({ ...c, appCount: appsByCompany[c.id] || 0 }))
        .sort((a, b) => b.appCount - a.appCount)
        .slice(0, 5);

      // ── Top companies by hired ───────────────────
      const hiredByCompany = {};
      apps.filter(a => a.status === 'hired').forEach(a => {
        if (a.company_id)
          hiredByCompany[a.company_id] =
            (hiredByCompany[a.company_id] || 0) + 1;
      });

      const topByHired = companies
        .filter(c => hiredByCompany[c.id])
        .map(c => ({ ...c, hiredCount: hiredByCompany[c.id] || 0 }))
        .sort((a, b) => b.hiredCount - a.hiredCount)
        .slice(0, 5);

      // ── Top companies by rejected ────────────────
      const rejectedByCompany = {};
      apps.filter(a => a.status === 'rejected').forEach(a => {
        if (a.company_id)
          rejectedByCompany[a.company_id] =
            (rejectedByCompany[a.company_id] || 0) + 1;
      });

      const topByRejected = companies
        .filter(c => rejectedByCompany[c.id])
        .map(c => ({ ...c, rejectedCount: rejectedByCompany[c.id] || 0 }))
        .sort((a, b) => b.rejectedCount - a.rejectedCount)
        .slice(0, 5);

      // ── Top companies by shortlisted ─────────────
      const shortlistedByCompany = {};
      apps.filter(a =>
        ['shortlisted', 'interview_scheduled'].includes(a.status)
      ).forEach(a => {
        if (a.company_id)
          shortlistedByCompany[a.company_id] =
            (shortlistedByCompany[a.company_id] || 0) + 1;
      });

      const topByShortlisted = companies
        .filter(c => shortlistedByCompany[c.id])
        .map(c => ({ ...c, shortlistedCount: shortlistedByCompany[c.id] || 0 }))
        .sort((a, b) => b.shortlistedCount - a.shortlistedCount)
        .slice(0, 5);

      // ── Most job-posting companies ───────────────
      const jobsByCompany = {};
      jobs.forEach(j => {
        if (j.company_id)
          jobsByCompany[j.company_id] =
            (jobsByCompany[j.company_id] || 0) + 1;
      });

      const topByJobs = companies
        .filter(c => jobsByCompany[c.id])
        .map(c => ({ ...c, jobCount: jobsByCompany[c.id] || 0 }))
        .sort((a, b) => b.jobCount - a.jobCount)
        .slice(0, 5);

      // ── Revenue estimate ─────────────────────────
      const planRevenue = {
        trial: 0, basic: 60, pro: 165, enterprise: 410
      };
      const monthlyRevenue = companies
        .filter(c => c.verification_status === 'verified')
        .reduce((sum, c) => sum + (planRevenue[c.plan || 'trial'] || 0), 0);

      // ── Recent companies ─────────────────────────
      const recentCompanies = [...companies]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 6);

      setData({
        totalCompanies, verifiedCompanies, pendingCompanies,
        activeJobs, totalJobs: jobs.length,
        totalApps, hiredCount, rejectedCount, shortlistedCount,
        conversionRate: totalApps > 0
          ? ((hiredCount / totalApps) * 100).toFixed(1) : '0',
        planBreakdown,
        companiesByCountry, jobsByCategory,
        applicationsByStatus,
        topByApps, topByHired, topByRejected,
        topByShortlisted, topByJobs,
        monthlyRevenue,
        recentCompanies,
        allCompanies: companies,
      });
    } catch (err) {
      console.error('Stats load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ['Metric', 'Value'],
      ['Total Companies', data.totalCompanies],
      ['Verified Companies', data.verifiedCompanies],
      ['Pending Review', data.pendingCompanies],
      ['Total Jobs', data.totalJobs],
      ['Active Jobs', data.activeJobs],
      ['Total Applications', data.totalApps],
      ['Hired', data.hiredCount],
      ['Rejected', data.rejectedCount],
      ['Shortlisted', data.shortlistedCount],
      ['Conversion Rate', `${data.conversionRate}%`],
      ['Est. Monthly Revenue', `$${data.monthlyRevenue}`],
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `platform_stats_${Date.now()}.csv`;
    a.click();
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1E293B' }}>
            Platform Analytics
          </h1>
          <p style={{ color: '#94A3B8', marginTop: 4, fontSize: 14 }}>
            UNIVERSE Careers · Complete platform insights
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {[7, 30, 90, 365].map(d => (
            <button key={d} onClick={() => setRange(d)} style={{
              padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              border: range === d ? '1.5px solid #4F8EF7' : '1.5px solid #E2E8F0',
              background: range === d ? 'rgba(79,142,247,0.1)' : '#fff',
              color: range === d ? '#4F8EF7' : '#64748B', cursor: 'pointer',
            }}>
              {d === 365 ? '1yr' : `${d}d`}
            </button>
          ))}
          <button onClick={exportCSV} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            border: '1.5px solid #E2E8F0', background: '#fff',
            color: '#64748B', cursor: 'pointer',
          }}>
            <Download size={13} />Export
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <div className="spinner" />
        </div>
      ) : data && (
        <>
          {/* ── KPI Cards Row 1 ── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))',
            gap: 14, marginBottom: 20,
          }}>
            {[
              { icon: <Building2 size={20} />, label: 'Total Companies', value: data.totalCompanies, color: '#4F8EF7', bg: 'rgba(79,142,247,0.08)', sub: `${data.verifiedCompanies} verified` },
              { icon: <CheckCircle size={20} />, label: 'Verified', value: data.verifiedCompanies, color: '#10B981', bg: 'rgba(16,185,129,0.08)', sub: `${data.pendingCompanies} pending` },
              { icon: <Briefcase size={20} />, label: 'Active Jobs', value: data.activeJobs, color: '#7C3AED', bg: 'rgba(124,58,237,0.08)', sub: `${data.totalJobs} total posted` },
              { icon: <Users size={20} />, label: 'Applications', value: data.totalApps, color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', sub: `${data.conversionRate}% hired` },
              { icon: <Award size={20} />, label: 'Hired', value: data.hiredCount, color: '#22D3EE', bg: 'rgba(34,211,238,0.08)', sub: 'students placed' },
              { icon: <Activity size={20} />, label: 'Est. Revenue/mo', value: `$${data.monthlyRevenue.toLocaleString()}`, color: '#EC4899', bg: 'rgba(236,72,153,0.08)', sub: 'from paid plans' },
            ].map((s, i) => (
              <div key={i} style={{
                background: '#fff', borderRadius: 14,
                padding: '18px 16px', border: '1px solid #E2E8F0',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{
                  width: 46, height: 46, borderRadius: 12,
                  background: s.bg, color: s.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#1E293B', lineHeight: 1 }}>
                    {s.value}
                  </div>
                  <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 3 }}>{s.label}</div>
                  <div style={{ fontSize: 10, color: s.color, marginTop: 2, fontWeight: 600 }}>{s.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Plan Distribution ── */}
          <div style={{
            background: '#fff', borderRadius: 16,
            border: '1px solid #E2E8F0', padding: '20px 24px',
            marginBottom: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'rgba(245,158,11,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Crown size={16} color="#F59E0B" />
              </div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1E293B' }}>
                Plan Distribution
              </div>
              <span style={{ fontSize: 12, color: '#94A3B8', marginLeft: 'auto' }}>
                {data.totalCompanies} companies total
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
              {['trial', 'basic', 'pro', 'enterprise'].map(pk => {
                const p = PLAN_CONFIG[pk];
                const count = data.planBreakdown[pk] || 0;
                const pct = data.totalCompanies > 0
                  ? Math.round((count / data.totalCompanies) * 100) : 0;
                return (
                  <div key={pk} style={{
                    background: p.bg, borderRadius: 12, padding: '14px 16px',
                    border: `1px solid ${p.color}25`,
                  }}>
                    <div style={{ fontSize: 22, marginBottom: 6 }}>{p.icon}</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: p.color }}>{count}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1E293B', marginTop: 2 }}>{p.label}</div>
                    <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{pct}% of total</div>
                    <div style={{
                      marginTop: 10, height: 4, background: '#F1F5F9', borderRadius: 2, overflow: 'hidden',
                    }}>
                      <div style={{
                        height: '100%', width: `${pct}%`,
                        background: p.color, borderRadius: 2,
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Main Grid ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

            {/* Applications by Status */}
            <SCard title="Applications by Status" icon={<Users size={15} />} color="#10B981">
              {data.applicationsByStatus.length === 0 ? (
                <Empty text="No applications yet" />
              ) : data.applicationsByStatus.map((item, i) => {
                const col = STATUS_COLORS[item.status] || '#94A3B8';
                const total = data.totalApps || 1;
                const pct = Math.round((item.count / total) * 100);
                return (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 10, height: 10, borderRadius: '50%', background: col, flexShrink: 0,
                        }} />
                        <span style={{ fontSize: 12, color: '#475569', fontWeight: 600, textTransform: 'capitalize' }}>
                          {item.status.replace('_', ' ')}
                        </span>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: col }}>
                        {item.count} ({pct}%)
                      </span>
                    </div>
                    <div style={{ height: 6, background: '#F1F5F9', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${pct}%`, background: col,
                        borderRadius: 3, transition: 'width 0.6s ease',
                      }} />
                    </div>
                  </div>
                );
              })}
            </SCard>

            {/* Jobs by Category */}
            <SCard title="Jobs by Category" icon={<Briefcase size={15} />} color="#7C3AED">
              {data.jobsByCategory.length === 0 ? (
                <Empty text="No jobs posted yet" />
              ) : data.jobsByCategory.map((item, i) => (
                <BarRow key={i}
                  label={item.category}
                  value={item.count}
                  max={data.jobsByCategory[0]?.count || 1}
                  color="#7C3AED" />
              ))}
            </SCard>

            {/* Companies by Country */}
            <SCard title="Companies by Country" icon={<Globe size={15} />} color="#4F8EF7">
              {data.companiesByCountry.length === 0 ? (
                <Empty text="No data" />
              ) : data.companiesByCountry.map((item, i) => (
                <BarRow key={i}
                  label={item.country || 'Unknown'}
                  value={item.count}
                  max={data.companiesByCountry[0]?.count || 1}
                  color="#4F8EF7" />
              ))}
            </SCard>

            {/* Hiring summary */}
            <SCard title="Hiring Funnel" icon={<TrendingUp size={15} />} color="#22D3EE">
              {[
                { label: 'Applications Received', value: data.totalApps, color: '#4F8EF7', icon: '📥' },
                { label: 'Shortlisted', value: data.shortlistedCount, color: '#10B981', icon: '✅' },
                { label: 'Interviewed', value: data.applicationsByStatus.find(a => a.status === 'interview_scheduled')?.count || 0, color: '#7C3AED', icon: '🤝' },
                { label: 'Hired', value: data.hiredCount, color: '#22D3EE', icon: '🎉' },
                { label: 'Rejected', value: data.rejectedCount, color: '#EF4444', icon: '❌' },
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '8px 0', borderBottom: i < 4 ? '1px solid #F1F5F9' : 'none',
                }}>
                  <span style={{ fontSize: 18 }}>{item.icon}</span>
                  <span style={{ flex: 1, fontSize: 13, color: '#64748B', fontWeight: 500 }}>
                    {item.label}
                  </span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: item.color }}>
                    {item.value}
                  </span>
                  {data.totalApps > 0 && (
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      color: item.color, minWidth: 36, textAlign: 'right',
                    }}>
                      {Math.round((item.value / data.totalApps) * 100)}%
                    </span>
                  )}
                </div>
              ))}
            </SCard>
          </div>

          {/* ── Company Rankings ── */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20,
          }}>
            {/* Most Applications */}
            <RankCard
              title="Most Applications Received"
              emoji="📥"
              color="#4F8EF7"
              items={data.topByApps}
              valueKey="appCount"
              valueLabel="apps"
            />

            {/* Most Hired */}
            <RankCard
              title="Most Students Hired"
              emoji="🎉"
              color="#10B981"
              items={data.topByHired}
              valueKey="hiredCount"
              valueLabel="hired"
            />

            {/* Most Shortlisted */}
            <RankCard
              title="Most Students Shortlisted"
              emoji="✅"
              color="#7C3AED"
              items={data.topByShortlisted}
              valueKey="shortlistedCount"
              valueLabel="shortlisted"
            />

            {/* Most Rejected */}
            <RankCard
              title="Most Rejections Issued"
              emoji="❌"
              color="#EF4444"
              items={data.topByRejected}
              valueKey="rejectedCount"
              valueLabel="rejected"
            />
          </div>

          {/* ── Most Active Companies (Job Posts) ── */}
          <div style={{
            background: '#fff', borderRadius: 16,
            border: '1px solid #E2E8F0', overflow: 'hidden', marginBottom: 20,
          }}>
            <div style={{
              padding: '18px 24px', borderBottom: '1px solid #F1F5F9',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'rgba(245,158,11,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Star size={15} color="#F59E0B" />
              </div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1E293B' }}>
                Most Active Companies (by Job Posts)
              </div>
            </div>
            {data.topByJobs.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
                No job data yet
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F8FAFF' }}>
                    {['#', 'Company', 'Plan', 'Jobs Posted', 'Applications', 'Country'].map(h => (
                      <th key={h} style={{
                        padding: '10px 16px', textAlign: 'left',
                        fontSize: 11, fontWeight: 700, color: '#94A3B8', letterSpacing: 0.5,
                        borderBottom: '1px solid #F1F5F9',
                      }}>{h.toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.topByJobs.map((c, i) => {
                    const pc = PLAN_CONFIG[c.plan || 'trial'];
                    return (
                      <tr key={c.id} style={{ borderBottom: '1px solid #F1F5F9' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F8FAFF'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: '50%',
                            background: i === 0 ? '#F59E0B' : i === 1 ? '#94A3B8' : '#CD7F32',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontSize: 11, fontWeight: 800,
                          }}>{i + 1}</div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                              width: 34, height: 34, borderRadius: 8,
                              background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0,
                            }}>
                              {c.name?.[0]?.toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B' }}>
                                {c.name}
                              </div>
                              <div style={{ fontSize: 11, color: '#94A3B8' }}>{c.contact_email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700,
                            padding: '3px 8px', borderRadius: 5,
                            background: pc.bg, color: pc.color,
                          }}>
                            {pc.icon} {pc.label}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontSize: 14, fontWeight: 800, color: '#F59E0B' }}>
                            {c.jobCount}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#4F8EF7' }}>
                            {c.slots_used || 0}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748B' }}>
                          {c.country}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Recent Companies ── */}
          <div style={{
            background: '#fff', borderRadius: 16,
            border: '1px solid #E2E8F0', overflow: 'hidden',
          }}>
            <div style={{
              padding: '18px 24px', borderBottom: '1px solid #F1F5F9',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'rgba(79,142,247,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Clock size={15} color="#4F8EF7" />
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1E293B' }}>
                  Recently Registered Companies
                </div>
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F8FAFF' }}>
                  {['Company', 'Plan', 'Status', 'Registered', 'Document'].map(h => (
                    <th key={h} style={{
                      padding: '10px 16px', textAlign: 'left',
                      fontSize: 11, fontWeight: 700, color: '#94A3B8', letterSpacing: 0.5,
                      borderBottom: '1px solid #F1F5F9',
                    }}>{h.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.recentCompanies.map((c, i) => {
                  const pc = PLAN_CONFIG[c.plan || 'trial'];
                  const STATUS_CONFIG = {
                    pending: { color: '#F59E0B', bg: '#FEF3C7', label: 'Pending' },
                    under_review: { color: '#3B82F6', bg: '#DBEAFE', label: 'Under Review' },
                    verified: { color: '#10B981', bg: '#D1FAE5', label: 'Verified' },
                    rejected: { color: '#EF4444', bg: '#FFE4E6', label: 'Rejected' },
                  };
                  const sc = STATUS_CONFIG[c.verification_status] || STATUS_CONFIG.pending;
                  return (
                    <tr key={c.id} style={{ borderBottom: '1px solid #F1F5F9' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F8FAFF'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: 8,
                            background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontWeight: 800, fontSize: 14,
                          }}>
                            {c.name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B' }}>
                              {c.name}
                            </div>
                            <div style={{ fontSize: 11, color: '#94A3B8' }}>{c.country}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          padding: '3px 8px', borderRadius: 5,
                          background: pc.bg, color: pc.color,
                        }}>
                          {pc.icon} {pc.label}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          padding: '3px 8px', borderRadius: 5,
                          background: sc.bg, color: sc.color,
                        }}>
                          {sc.label}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748B' }}>
                        {new Date(c.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {c.reg_certificate_url ? (
                          <a href={c.reg_certificate_url}
                            target="_blank" rel="noreferrer"
                            onClick={async (e) => {
                              // If URL doesn't work directly, generate signed URL
                              if (c.reg_certificate_url.includes('supabase')) {
                                e.preventDefault();
                                try {
                                  // Extract path from URL
                                  const urlParts = c.reg_certificate_url.split('/avatars/');
                                  if (urlParts.length > 1) {
                                    const path = decodeURIComponent(urlParts[1].split('?')[0]);
                                    const { data: signedData } = await supabase.storage
                                      .from('avatars')
                                      .createSignedUrl(path, 3600);
                                    if (signedData?.signedUrl) {
                                      window.open(signedData.signedUrl, '_blank');
                                    } else {
                                      window.open(c.reg_certificate_url, '_blank');
                                    }
                                  } else {
                                    window.open(c.reg_certificate_url, '_blank');
                                  }
                                } catch (_) {
                                  window.open(c.reg_certificate_url, '_blank');
                                }
                              }
                            }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '5px 10px',
                              background: 'rgba(16,185,129,0.1)',
                              border: '1px solid rgba(16,185,129,0.25)',
                              borderRadius: 7, color: '#10B981',
                              fontSize: 11, fontWeight: 700, textDecoration: 'none',
                            }}>
                            📄 View Doc
                          </a>
                        ) : (
                          <span style={{ fontSize: 11, color: '#94A3B8' }}>Not uploaded</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

// ── Shared Components ─────────────────────────────────

function SCard({ title, icon, color, children }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 16,
      border: '1px solid #E2E8F0', overflow: 'hidden',
    }}>
      <div style={{
        padding: '16px 20px', borderBottom: '1px solid #F1F5F9',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8,
          background: `${color}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', color,
        }}>{icon}</div>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#1E293B' }}>{title}</div>
      </div>
      <div style={{ padding: '16px 20px' }}>{children}</div>
    </div>
  );
}

function RankCard({ title, emoji, color, items, valueKey, valueLabel }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 16,
      border: '1px solid #E2E8F0', overflow: 'hidden',
    }}>
      <div style={{
        padding: '16px 20px', borderBottom: '1px solid #F1F5F9',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 20 }}>{emoji}</span>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#1E293B' }}>{title}</div>
      </div>
      <div style={{ padding: '12px 20px' }}>
        {items.length === 0 ? (
          <Empty text="No data yet" />
        ) : items.map((c, i) => {
          const pc = PLAN_CONFIG[c.plan || 'trial'];
          return (
            <div key={c.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '9px 0',
              borderBottom: i < items.length - 1 ? '1px solid #F1F5F9' : 'none',
            }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                background: i === 0 ? '#F59E0B' : i === 1 ? '#94A3B8' : '#CD7F32',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 10, fontWeight: 800,
              }}>{i + 1}</div>
              <div style={{
                width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 800, fontSize: 13,
              }}>
                {c.name?.[0]?.toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 700, fontSize: 13, color: '#1E293B',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{c.name}</div>
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '1px 5px',
                  borderRadius: 3, background: pc.bg, color: pc.color,
                }}>
                  {pc.icon} {pc.label}
                </span>
              </div>
              <div style={{
                fontSize: 16, fontWeight: 800, color,
                display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0,
              }}>
                {c[valueKey]}
                <span style={{ fontSize: 9, color: '#94A3B8', fontWeight: 500 }}>
                  {valueLabel}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BarRow({ label, value, max, color }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      paddingBottom: 8, marginBottom: 8, borderBottom: '1px solid #F1F5F9',
    }}>
      <span style={{
        fontSize: 12, color: '#64748B',
        minWidth: 90, overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{label}</span>
      <div style={{
        flex: 1, height: 6, background: '#F1F5F9', borderRadius: 3, overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${Math.round((value / max) * 100)}%`,
          background: color, borderRadius: 3,
          transition: 'width 0.6s ease',
        }} />
      </div>
      <span style={{
        fontSize: 12, fontWeight: 700, color: '#1E293B', minWidth: 20, textAlign: 'right',
      }}>{value}</span>
    </div>
  );
}

function Empty({ text }) {
  return (
    <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: 13, padding: '20px 0' }}>
      {text}
    </div>
  );
}